import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { CreateVolumeRequest } from '@xver/apis/v1/kufer/volumes/model';
import { getErrorMessage } from '@xver/utils/error';
import { useTranslation } from 'react-i18next';

import { CONTAINER_DETAIL_QUERY_KEY } from './useContainerQuery';
import { VOLUMES_QUERY_KEY } from './useVolumesQuery';

const CONTAINER_LIST_QUERY_KEY = ['apis.v1.kufer.containers.list'];

export function useVolumeMutations(containerId: string | undefined) {
    const { t } = useTranslation('containerEngineInstance');
    const queryClient = useQueryClient();

    const invalidateVolumes = async () => {
        await queryClient.invalidateQueries({ queryKey: [VOLUMES_QUERY_KEY, containerId] });
    };

    const invalidateVolumesAndContainer = async () => {
        await Promise.all([
            invalidateVolumes(),
            queryClient.invalidateQueries({ queryKey: [CONTAINER_DETAIL_QUERY_KEY, containerId] }),
            queryClient.invalidateQueries({ queryKey: CONTAINER_LIST_QUERY_KEY }),
        ]);
    };

    const showError = (title: string) => (err: unknown) => {
        notifications.show({
            title,
            message: getErrorMessage(err),
            icon: <IconExclamationCircle size={18} color="#eb5757" />,
        });
    };

    const createVolume = useMutation({
        mutationFn: (data: CreateVolumeRequest) =>
            Endpoints.v1.Kufer.Volumes.createVolume(containerId as string, data),
        onSuccess: async () => {
            notifications.show({
                title: t('volumes.addSuccessTitle', 'Volume added'),
                message: t(
                    'volumes.addSuccessMessage',
                    'The container is redeploying to mount the volume.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidateVolumesAndContainer();
        },
        onError: showError(t('volumes.addFailedTitle', 'Failed to add volume')),
    });

    const resizeVolume = useMutation({
        mutationFn: ({ volumeId, sizeGiB }: { volumeId: string; sizeGiB: number }) =>
            Endpoints.v1.Kufer.Volumes.resizeVolume(containerId as string, volumeId, {
                size_gib: sizeGiB,
            }),
        onSuccess: async (volume) => {
            notifications.show({
                title: t('volumes.resizeSuccessTitle', 'Volume resized'),
                message: t('volumes.resizeSuccessMessage', {
                    size: volume.size_gib,
                    defaultValue: 'The volume was grown to {{size}} GiB.',
                }),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidateVolumes();
        },
        onError: showError(t('volumes.resizeFailedTitle', 'Failed to resize volume')),
    });

    const deleteVolume = useMutation({
        mutationFn: (volumeId: string) =>
            Endpoints.v1.Kufer.Volumes.deleteVolume(containerId as string, volumeId),
        onSuccess: async () => {
            notifications.show({
                title: t('volumes.deleteSuccessTitle', 'Volume deleted'),
                message: t(
                    'volumes.deleteSuccessMessage',
                    'The volume and all of its data have been removed.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidateVolumesAndContainer();
        },
        onError: showError(t('volumes.deleteFailedTitle', 'Failed to delete volume')),
    });

    return { createVolume, resizeVolume, deleteVolume };
}
